import { useState, type FC } from "react"

import useUpdateProposalMargin from "~/api/hooks/useUpdateProposalMargin"
import useUpdateProposalTotalCost from "~/api/hooks/useUpdateProposalTotalCost"
import { SimpleNumberInputInline } from "~/components/Input/SimpleNumberInput"
import {
  NOTIFICATION_COLORS,
  NotificationMessage,
} from "~/containers/Proposal/state/notifications/notifications"
import { useProposalActions } from "~/containers/Proposal/state/proposal/proposal-context"

import {
  ExciseTaxRow,
  PricingItemRow,
  PricingTableContainer,
  SalesTaxRow,
} from "./SystemPricingShared"
import type { PricingTableProps } from "./systemPricingTypes"
import { formatters, validateMarginCosts } from "./utils"
import type { SystemPricingItem } from "./utils/system-pricings.utils"

interface PricingVariantConfig {
  readonly allowTotalCostEdit: boolean
}

const PRICING_VARIANTS: Record<string, PricingVariantConfig> = {
  "admin": {
    allowTotalCostEdit: true,
  },
  "non-enterprise": {
    allowTotalCostEdit: true,
  },
} as const

const NonEnterpriseSystemPricingRegular: FC<PricingTableProps> = ({
  maxMargin,
  minimumMargin,
  proposalId,
  variant,
  proposalPrice,
}) => {
  const config = PRICING_VARIANTS[variant]
  const updateProposal = useUpdateProposalMargin()
  const updateProposalTotalCost = useUpdateProposalTotalCost()

  // Store full precision margin - do NOT round for state
  const [userAdjustedMargin, setUserAdjustedMargin] = useState<number | null>(
    proposalPrice.marginRate,
  )

  const [editingTotalCost, setEditingTotalCost] = useState<number | null>(null)
  const [isEditingTotal, setIsEditingTotal] = useState(false)

  const [loadingState, setLoadingState] = useState<
    `idle` | `margin` | `totalCost`
  >(`idle`)

  const { changeLocal } = useProposalActions()

  // Derive the displayed total cost: use editing value if actively editing, otherwise use prop
  const displayTotalCost = isEditingTotal
    ? editingTotalCost
    : proposalPrice.total

  const marginError = validateMarginCosts(
    userAdjustedMargin ?? proposalPrice.marginRate,
    minimumMargin,
    maxMargin,
  )

  const handleMarginUpdateBlur = async (
    newMargin: number | null,
  ): Promise<void> => {
    if (typeof newMargin !== `number` || isNaN(newMargin)) {
      return
    }

    // Use tolerance-based comparison to handle floating point precision
    // Only skip update if values are truly the same (within 0.000001 tolerance)
    const MARGIN_TOLERANCE = 0.000001
    if (Math.abs(newMargin - proposalPrice.marginRate) < MARGIN_TOLERANCE) {
      return
    }

    const marginErrorMessage = validateMarginCosts(
      newMargin,
      minimumMargin,
      maxMargin,
    )
    if (marginErrorMessage) {
      console.error(`failed to update margin ${marginErrorMessage}`)
      return
    }

    try {
      setLoadingState(`margin`)
      await updateProposal.mutateAsync({
        id: proposalId,
        margin: newMargin,
      })
      setLoadingState(`idle`)

      // Reset editing state since margin change updates the total cost
      setIsEditingTotal(false)
      setEditingTotalCost(null)

      changeLocal({ margin: newMargin })
    } catch (error) {
      setLoadingState(`idle`)
      console.error(`Failed to fetch pricing for new margin:`, error)
    }
  }

  const handleCurrentTotalCostUpdateBlur = async (
    newTotalCost: number | null,
  ): Promise<void> => {
    if (typeof newTotalCost !== `number` || isNaN(newTotalCost)) {
      // Reset editing state if invalid input
      setIsEditingTotal(false)
      setEditingTotalCost(null)
      return
    }

    // If the value hasn't changed, just reset editing state
    if (newTotalCost === proposalPrice.total) {
      setIsEditingTotal(false)
      setEditingTotalCost(null)
      return
    }

    try {
      setLoadingState(`totalCost`)
      const pricing = await updateProposalTotalCost.mutateAsync({
        id: proposalId,
        totalCost: newTotalCost,
      })

      setLoadingState(`idle`)
      setIsEditingTotal(false)
      setEditingTotalCost(null)
      setUserAdjustedMargin(formatters.roundMargin(pricing.sellerMarginRate))

      const newMarginError = validateMarginCosts(
        pricing.sellerMarginRate,
        minimumMargin,
        maxMargin,
      )
      if (newMarginError) {
        console.error(newMarginError)
        return
      }

      changeLocal({
        margin: pricing.sellerMarginRate,
      })
    } catch (error) {
      setLoadingState(`idle`)
      console.error(`Failed to fetch pricing for new total cost:`, error)
      return
    }
  }

  const displayItems = proposalPrice.lineItems.filter(
    (item: SystemPricingItem) => {
      const isNonZero = item.total !== 0

      return isNonZero
    },
  )

  return (
    <>
      <NotificationMessage
        {...NOTIFICATION_COLORS.danger}
        style={{
          opacity: marginError ? 1 : 0,
          transition: `opacity 0.2s ease`,
          pointerEvents: marginError ? `auto` : `none`,
          marginBottom: `.5rem`,
        }}
      >
        <p>{marginError ?? `\u00A0`}</p>
      </NotificationMessage>
      <PricingTableContainer>
        <thead className="sticky top-0 z-30 border-gray-200 bg-gray-100">
          <tr className="text-sm font-extralight text-gray-500">
            <th className="px-6 py-2 text-left">Item</th>
            <th className="px-6 py-2 text-end">Price/Watt</th>
            <th className="px-6 py-2 text-right">Total</th>
          </tr>
        </thead>
        <tbody className="table-border-show divide-y divide-gray-200">
          {displayItems.map((item: SystemPricingItem) => (
            <PricingItemRow key={item.id} item={item} />
          ))}
        </tbody>
        <tfoot className="sticky bottom-0 z-20 bg-white">
          <tr key={`margin`} className="transition-colors hover:bg-gray-50">
            <td
              className={`border border-t-0 border-r-0 border-l-0 border-solid border-b-gray-200 px-6 py-4 text-sm font-medium text-gray-800`}
            >
              Margin
            </td>
            <td
              className={`border border-t-0 border-r-0 border-l-0 border-solid border-b-gray-200 px-6 py-4 text-center text-sm text-gray-600`}
            >
              <SimpleNumberInputInline
                value={userAdjustedMargin}
                setValue={setUserAdjustedMargin}
                min={0}
                onBlur={handleMarginUpdateBlur}
                loading={loadingState === `margin`}
              />
            </td>
            <td
              className={`border border-t-0 border-r-0 border-l-0 border-solid border-b-gray-200 px-6 py-4 text-right text-sm text-gray-800`}
            >
              {formatters.currency(
                (userAdjustedMargin ?? 0) * proposalPrice.systemSize,
              )}
            </td>
          </tr>
          <SalesTaxRow
            salesTax={proposalPrice.salesTax}
            systemSizeInWatts={proposalPrice.systemSize}
          />
          <ExciseTaxRow
            exciseTaxRate={proposalPrice.generalExciseTaxRate}
            exciseTax={proposalPrice.generalExciseTax}
          />
          {config.allowTotalCostEdit ? (
            <tr key="total-cost" className="bg-white">
              <td
                className={`border border-t-0 border-r-0 border-l-0 border-solid border-b-gray-200 px-6 py-4 text-sm font-medium text-gray-800`}
              >
                Total Cost
              </td>
              <td
                className={`border border-t-0 border-r-0 border-l-0 border-solid border-b-gray-200 px-6 py-4 text-end text-sm text-gray-600`}
              >
                {formatters.pricePerWatt(
                  proposalPrice.total / proposalPrice.systemSize,
                )}
              </td>
              <td
                className={`border border-t-0 border-r-0 border-l-0 border-solid border-b-gray-200 px-6 py-4 text-right text-sm font-medium text-gray-800`}
              >
                <SimpleNumberInputInline
                  value={displayTotalCost}
                  setValue={(value) => {
                    setEditingTotalCost(value)
                    setIsEditingTotal(true)
                  }}
                  min={0}
                  onBlur={handleCurrentTotalCostUpdateBlur}
                  loading={loadingState === `totalCost`}
                />
              </td>
            </tr>
          ) : (
            <tr key="total-cost" className="bg-white">
              <td
                className={`border border-t-0 border-r-0 border-l-0 border-solid border-b-gray-200 px-6 py-4 text-sm font-medium text-gray-800`}
              >
                Total Cost
              </td>
              <td
                className={`border border-t-0 border-r-0 border-l-0 border-solid border-b-gray-200 px-6 py-4 text-end text-sm text-gray-600`}
              >
                {formatters.pricePerWatt(
                  proposalPrice.total / proposalPrice.systemSize,
                )}
              </td>
              <td
                className={`border border-t-0 border-r-0 border-l-0 border-solid border-b-gray-200 px-6 py-4 text-right text-sm text-gray-800`}
              >
                {formatters.currency(proposalPrice.total || 0)}
              </td>
            </tr>
          )}
        </tfoot>
      </PricingTableContainer>
    </>
  )
}

export default NonEnterpriseSystemPricingRegular
